import { useState, useEffect, useCallback } from "react";
import { api, API, getToken, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TeamAdmin } from "@/components/TeamAdmin";

import {
  Database,
  DownloadCloud,
  History,
  Loader2,
  RotateCcw,
  ShieldAlert,
  Save,
  Clock,
} from "lucide-react";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const REASON_LABEL = {
  "daily-auto": "Daily auto-backup",
  manual: "Manual backup",
  "pre-restore-safety": "Safety (before restore)",
  "pre-reset-safety": "Safety (before reset)",
};

export default function BackupRestore() {
  const { user } = useAuth();
  const isSuperAdmin = ["super admin", "admin"].includes(String(user?.role || "").toLowerCase());
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [confirm, setConfirm] = useState(null); // {type:'restore'|'reset', backup}
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backups");
      setBackups(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load backups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) load();
  }, [load, isSuperAdmin]);

  const createBackup = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/backups");
      toast.success(`Backup created · ${data.total} records`);
      load();
    } catch {
      toast.error("Backup failed");
    } finally {
      setCreating(false);
    }
  };

  const download = (id) =>
    window.open(`${API}/backups/${id}/download?auth=${getToken()}`, "_blank");

  const doAction = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      if (confirm.type === "restore") {
        const { data } = await api.post(`/backups/${confirm.backup.id}/restore`);
        const total = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
        toast.success(`Restored ${total} records. Reloading…`);
      } else {
        await api.post("/reset");
        toast.success("Workspace reset. Reloading…");
      }
      setConfirm(null);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="backup-page">
      {!isSuperAdmin ? (
        <Card className="p-8 text-center">
          <ShieldAlert className="h-8 w-8 text-warning mx-auto mb-3" />
          <h2 className="font-heading text-lg font-semibold">Super Admin only</h2>
          <p className="text-sm text-muted-foreground mt-1">This page manages backups, workspace reset and team members. You need Super Admin access.</p>
        </Card>
      ) : (
        <>
      <TeamAdmin />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-md bg-brand/10 border border-brand/25 flex items-center justify-center mt-0.5">
            <Database className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Backup & Restore</h1>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> Automatic daily snapshots · last 30 retained
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              setExporting(true);
              try {
                const { data: health } = await api.get("/exports/health");
                await downloadFile("/exports/workbook", "ethara_books.xlsx");
                toast.success(`Exported ${Object.keys(health.tables).length} tables · ${health.total} rows`);
              } catch {
                toast.error("Export failed");
              } finally {
                setExporting(false);
              }
            }}
            disabled={exporting}
            data-testid="export-everything-btn"
            className="gap-2"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Export Everything (XLSX)
          </Button>
          <Button onClick={createBackup} disabled={creating} data-testid="create-backup-btn" className="gap-2">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Backup Now
          </Button>
        </div>
      </div>

      {/* Backups list */}
      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <History className="h-4 w-4 text-brand" />
          <h3 className="font-heading font-semibold">Snapshots</h3>
          <span className="text-xs text-muted-foreground ml-auto">{backups.length} available</span>
        </div>
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : backups.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">No backups yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {backups.map((b) => (
              <div key={b.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/40" data-testid="backup-row">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{REASON_LABEL[b.reason] || b.reason}</p>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{fmt(b.created_at)} · {b.total} records</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => download(b.id)} className="gap-1.5" data-testid="download-backup-btn">
                  <DownloadCloud className="h-4 w-4" /> <span className="hidden sm:inline">Download</span>
                </Button>
                {isSuperAdmin && (
                  <Button variant="outline" size="sm" onClick={() => setConfirm({ type: "restore", backup: b })} className="gap-1.5" data-testid="restore-backup-btn">
                    <RotateCcw className="h-4 w-4" /> Restore
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Danger zone */}
      {isSuperAdmin && (
        <Card className="p-5 border-danger/30 bg-danger/[0.04]" data-testid="danger-zone">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-danger/10 border border-danger/30 flex items-center justify-center shrink-0">
                <ShieldAlert className="h-5 w-5 text-danger" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-danger">Reset Application</h3>
                <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">
                  Permanently deletes ALL financial data (banks, income, expenses, receivables, payables,
                  vendors, investments, FIRCs, attachments). A safety backup is taken first. User accounts are kept.
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setConfirm({ type: "reset" })} className="border-danger/40 text-danger hover:bg-danger/10 gap-2 shrink-0" data-testid="reset-app-btn">
              <RotateCcw className="h-4 w-4" /> Reset to Empty
            </Button>
          </div>
        </Card>
      )}

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-danger" />
              {confirm?.type === "reset" ? "Reset entire workspace?" : "Restore this backup?"}
            </DialogTitle>
            <DialogDescription>
              {confirm?.type === "reset"
                ? "This wipes all current financial data so the team starts fresh. A safety backup is created automatically before reset."
                : `This replaces ALL current data with the snapshot from ${confirm ? fmt(confirm.backup.created_at) : ""}. A safety backup of the current state is created first.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={working}>Cancel</Button>
            <Button onClick={doAction} disabled={working} className="bg-danger text-white hover:bg-danger/90 gap-2" data-testid="confirm-destructive-btn">
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              {confirm?.type === "reset" ? "Yes, reset everything" : "Yes, restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}
